import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { useReducer } from "react";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import type { ProfileSearchParams } from "common/types";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useProfilesPreview from "../../../../datahooks/useProfilesPreview";
import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import SectionHeading from "../../../SectionHeading";
import StepperNextButton from "../../../StepperNextButton";
import Stepper from "../../Stepper";
import MinerAccountSelector from "../MinerAccountSelector";
import PasteUrlTargeting from "./PasteUrlTargeting";
import PreviewResults from "./PreviewResults";
import SearchParams from "./SearchParams";
import { SearchParamsProvider } from "./SearchParamsContext";
import TargetingInfo from "./TargetingInfo";

const defaultSearchParams: ProfileSearchParams = {
  keywords: [],
  locations: [],
  industries: [],
  current_companies: [],
  past_companies: [],
  followers_of: null,
  schools: [],
  languages: [],
  skills: [],
};

export default function SearchPreview({
  initialParams,
}: {
  initialParams: ProfileSearchParams | undefined;
}) {
  const { outreach_type: outreachType } = useCampaignContext();
  const isDrip = outreachType === "drip";

  const [searchParams, setSearchParams] = useReducer(
    (
      prevState: ProfileSearchParams,
      newState: Partial<ProfileSearchParams>,
    ) => ({
      ...prevState,
      ...newState,
    }),
    initialParams || defaultSearchParams,
  );

  const { setTargeting, isSettingTargeting } = useTargetingMutations();

  const hasSearchParams = Object.values(searchParams).some((param) =>
    Array.isArray(param) ? param.length : param,
  );

  const {
    profiles,
    isLoadingProfiles,
    totalCount,
    isLimitExceeded,
    isProfilesPreviewError,
    refetchProfilesPreview,
  } = useProfilesPreview({
    searchParams,
  });

  const resetSearchParamsButton = hasSearchParams && (
    <Button
      size="sm"
      variant="secondary-black"
      className="max-lg:ml-auto"
      leftIcon={<ArrowCounterClockwise />}
      onClick={() => setSearchParams(defaultSearchParams)}
    >
      Reset all
    </Button>
  );

  return (
    <>
      <SearchParamsProvider value={{ searchParams, setSearchParams }}>
        {/* Heading */}
        <section className="lg:border-b lg:border-dashed lg:border-b-black-200 flex flex-col items-center justify-between gap-4 lg:flex-row lg:pb-4">
          <SectionHeading
            title="Targeting Setup"
            subtitle="Find targets for this campaign by adding relevant keywords and
              filters. You can add up to 6 keywords and unlimited filters"
          />

          <div className="flex w-full items-center justify-end gap-x-2.5 lg:gap-x-1.5">
            {!isDrip && (
              <>
                <div className="flex items-center">
                  <span className="mr-1 inline-flex text-button-xs">
                    Search account:
                  </span>

                  <MinerAccountSelector className="lg:w-[130px]" size="sm" />
                </div>

                <span className="h-8 w-px bg-black-200" />
              </>
            )}

            <PasteUrlTargeting hasSearchParams={hasSearchParams} />
          </div>

          <RenderIf condition={hasSearchParams}>
            <Button
              size="sm"
              variant="secondary-black"
              className="ml-auto lg:hidden"
              leftIcon={<ArrowCounterClockwise />}
              onClick={() => setSearchParams(defaultSearchParams)}
            >
              Reset all
            </Button>
          </RenderIf>
        </section>

        {/* Filters */}
        <SearchParams
          totalCount={totalCount}
          isLoadingProfiles={isLoadingProfiles}
          resetSearchParamsButton={resetSearchParamsButton}
        />

        {isProfilesPreviewError ? (
          <ErrorPlaceholder
            errorMessage="Error loading search preview results"
            onRetry={refetchProfilesPreview}
          />
        ) : (
          <PreviewResults profiles={profiles} isLoading={isLoadingProfiles} />
        )}
      </SearchParamsProvider>

      <RenderIf condition={profiles && !!profiles.length}>
        <TargetingInfo isLimitExceeded={isLimitExceeded} />
      </RenderIf>

      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            disabled={!profiles || !profiles.length}
            isLoading={isSettingTargeting}
            onClick={() => setTargeting({ campaignTargeting: searchParams })}
          />
        }
      />
    </>
  );
}
